import type { Application } from "../schema/Application.js";
import type { Competition } from "../schema/Competition.js";

export function czechAccountNumberToIban(accountNumber: string): string | null {
	const match = /^(?:(?<prefix>\d{1,6})-)?(?<account>\d{2,10})\/(?<bankCode>\d{4})$/u.exec(
		accountNumber.replace(/\s/gu, ""),
	);
	if (!match?.groups) {
		return null;
	}

	const { prefix = "0", account = "", bankCode = "" } = match.groups;
	const bban = bankCode + prefix.padStart(6, "0") + account.padStart(10, "0");
	const checkString = `${bban}123500`;

	let remainder = 0;
	for (const ch of checkString) {
		remainder = (remainder * 10 + Number(ch)) % 97;
	}
	const checkDigits = (98 - remainder).toString().padStart(2, "0");
	return `CZ${checkDigits}${bban}`;
}

export function buildSpaydString(
	iban: string,
	amountCzk: number,
	variableSymbol?: string,
	constantSymbol?: string,
	message?: string,
): string {
	const parts = ["SPD*1.0", `ACC:${iban.replace(/\s/gu, "")}`, `AM:${amountCzk.toFixed(2)}`, "CC:CZK"];
	if (variableSymbol) {
		parts.push(`X-VS:${variableSymbol}`);
	}
	if (constantSymbol) {
		parts.push(`X-KS:${constantSymbol}`);
	}
	if (message) {
		parts.push(`MSG:${message.slice(0, 60)}`);
	}
	return `${parts.join("*")}*`;
}

function normalizeAscii(text: string): string {
	return text
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/gu, "")
		.replace(/[^\x20-\x7e]/gu, "")
		.replace(/\s+/gu, " ")
		.trim();
}

export function buildEpcString(
	bic: string,
	beneficiaryName: string,
	iban: string,
	amountEur: number | undefined,
	variableSymbol?: string,
	constantSymbol?: string,
	message?: string,
): string {
	const remittance = [variableSymbol ? `VS:${variableSymbol}` : "", message ?? ""].filter(Boolean).join(" ");
	const lines = [
		"BCD",
		"002",
		"1",
		"SCT",
		bic,
		normalizeAscii(beneficiaryName).slice(0, 70),
		iban.replace(/\s/gu, ""),
		amountEur !== undefined ? `EUR${amountEur.toFixed(2)}` : "",
		"",
		constantSymbol ? `KS: ${normalizeAscii(constantSymbol)}` : "",
		remittance ? normalizeAscii(remittance).slice(0, 140) : "",
	];
	return lines.join("\n");
}

export function calculateApplicationTotalPrice(application: Application): number {
	return application.categories.reduce((sum, cat) => sum + cat.price, 0);
}

export interface PaymentQrStrings {
	czech: string | null;
	international: string | null;
}

export function getPaymentQrStrings(
	competition: Competition,
	totalCzk: number,
	variableSymbol?: string,
): PaymentQrStrings {
	const { payment } = competition;
	if (!payment) {
		return { czech: null, international: null };
	}

	const beneficiaryName = `${competition.organizer.firstName} ${competition.organizer.lastName}`;
	const message = competition.headline.cs;

	let czech: string | null = null;
	if (payment.bankAccountNumber) {
		const ibanForSpayd = czechAccountNumberToIban(payment.bankAccountNumber);
		if (ibanForSpayd) {
			czech = buildSpaydString(ibanForSpayd, totalCzk, variableSymbol, payment.constantSymbol, message);
		}
	}

	let international: string | null = null;
	if (payment.iban && payment.bic) {
		const amountEur = payment.eurRate !== undefined ? totalCzk / payment.eurRate : undefined;
		international = buildEpcString(
			payment.bic,
			beneficiaryName,
			payment.iban,
			amountEur,
			variableSymbol,
			payment.constantSymbol,
			message,
		);
	}

	return { czech, international };
}
